"use client";

import { useState } from "react";
import { deriveCoastalBriefing, type CoastalBriefingInput, type Severity } from "@/lib/conditions/local-briefing";

const SEVERITY_STYLE: Record<Severity, { box: string; title: string; icon: string }> = {
  danger: { box: "bg-red-50 border-red-200", title: "text-red-700", icon: "⚠️" },
  caution: { box: "bg-amber-50 border-amber-200", title: "text-amber-800", icon: "⚠️" },
  info: { box: "bg-warm-bg border-border-light", title: "text-navy", icon: "ℹ️" },
};

export function LocalBriefing({ input }: { input: CoastalBriefingInput }) {
  const briefing = deriveCoastalBriefing(input);
  const hasSerious = briefing.hazards.some((h) => h.severity !== "info");
  const [open, setOpen] = useState(hasSerious);

  const hasBody = briefing.hazards.length > 0 || briefing.spotNotes.length > 0;

  return (
    <div id="section-heads-up" className="scroll-mt-28 mt-4 bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-1">Heads up</div>
          <p className="text-[14px] md:text-[15px] font-semibold text-navy leading-snug max-w-[640px]">{briefing.headline}</p>
        </div>
        {hasBody && (
          <button
            onClick={() => setOpen((o) => !o)}
            className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border-light bg-white px-3 py-1 text-[12px] font-semibold text-navy hover:bg-warm-bg transition-colors"
            aria-expanded={open}
          >
            {open ? "Less" : "Details"}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`transition-transform ${open ? "rotate-180" : ""}`}>
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {open && hasBody && (
        <div className="mt-4 space-y-2.5">
          {briefing.hazards.map((h) => {
            const s = SEVERITY_STYLE[h.severity];
            return (
              <div key={h.key} className={`rounded-[10px] border px-3.5 py-3 ${s.box}`}>
                <div className={`text-[13px] font-semibold ${s.title}`}>
                  <span aria-hidden>{s.icon}</span> {h.title}
                </div>
                <div className="text-[12px] text-text-muted mt-0.5 leading-relaxed">{h.detail}</div>
              </div>
            );
          })}

          {briefing.spotNotes.length > 0 && (
            <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {briefing.spotNotes.map((n) => (
                <div key={n.key} className="text-[12px]">
                  <span className="font-semibold text-navy">{n.label}: </span>
                  <span className="text-text-muted">{n.value}</span>
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] text-text-muted/80 pt-1">
            Indicators derived from forecast data, not official warnings. Always check local lifeguard or official advice.
          </p>
        </div>
      )}
    </div>
  );
}
